// To organize imports in a better way
/* eslint-disable import/order */
import 'reflect-metadata'; // include once in the app for inversify (see: https://github.com/inversify/InversifyJS/blob/master/README.md#-installation)
import { ENTITLEMENT, EPG_TYPE, INTEGRATION } from '../constants';
import { container } from './container';
import { DETERMINE_ENTITLEMENT_TYPE, DETERMINE_INTEGRATION_TYPE, ENTITLEMENT_TYPE, INTEGRATION_TYPE } from './types';

import ContentApiService from '../services/ContentApiService';
import WatchHistoryService from '../services/WatchHistoryService';
import GenericEntitlementService from '../services/entitlement/GenericEntitlementService';
import FavoriteService from '../services/FavoriteService';
import ConfigService from '../services/ConfigService';
import SettingsService from '../services/SettingsService';

import WatchHistoryController from '../controllers/WatchHistoryController';
import CheckoutController from '../controllers/CheckoutController';
import AccountController from '../controllers/AccountController';
import FavoritesController from '../controllers/FavoritesController';
import AppController from '../controllers/AppController';
import EpgController from '../controllers/EpgController';
import EntitlementController from '../controllers/EntitlementController';
import { getIntegrationType } from './functions/getIntegrationType';
import {
  isCleengIntegrationType,
  isCognitoStripeIntegrationType,
  isJwpIntegrationType,
  isStreamIQIntegrationType,
  isVdOttIntegrationType,
} from './functions/calculateIntegrationType';
import { isAWSCognitoEntitlementType, isGenericEntitlementType } from './functions/calculateEntitlementType';

// Epg services
import EpgService from '../services/EpgService';
import ViewNexaEpgService from '../services/epg/ViewNexaEpgService';
import JWEpgService from '../services/epg/JWEpgService';

// Integration interfaces
import AccountService from '../services/integrations/AccountService';
import CheckoutService from '../services/integrations/CheckoutService';
import SubscriptionService from '../services/integrations/SubscriptionService';

// Cleeng integration
import CleengService from '../services/integrations/cleeng/CleengService';
import CleengAccountService from '../services/integrations/cleeng/CleengAccountService';
import CleengCheckoutService from '../services/integrations/cleeng/CleengCheckoutService';
import CleengSubscriptionService from '../services/integrations/cleeng/CleengSubscriptionService';

// JWP integration
import JWPAPIService from '../services/integrations/jwp/JWPAPIService';
import JWPAccountService from '../services/integrations/jwp/JWPAccountService';
import JWPCheckoutService from '../services/integrations/jwp/JWPCheckoutService';
import JWPSubscriptionService from '../services/integrations/jwp/JWPSubscriptionService';

// VD OTT integration
import VDApiService from '../services/integrations/vd/VDApiService';
import VDAccountService from '../services/integrations/vd/VDAccountService';

// Cognito Stripe integration
import AWSCognitoAccountService from '../services/integrations/cognitoStripe/AWSCognitoAccountService';
import AWSStripeSubscriptionService from '../services/integrations/cognitoStripe/AWSStripeSubscriptionService';
import AWSStripeCheckoutService from '../services/integrations/cognitoStripe/AWSStripeCheckoutService';
import AWSCognitoEntitlementService from '../services/integrations/cognitoStripe/AWSCognitoEntitlementService';

// Stream IQ integration
import StreamIQApiService from '../services/integrations/streamiq/StreamIQApiService';
import StreamIQData from '../services/integrations/streamiq/StreamIQData';
import StreamIQAccountService from '../services/integrations/streamiq/StreamIQAccountService';
import StreamIQEntitlementService from '../services/integrations/streamiq/StreamIQEntitlementService';
import StreamIQEpgService from '../services/integrations/streamiq/StreamIQEpgService';
import StreamIQSubscriptionService from '../services/integrations/streamiq/StreamIQSubscriptionService';
import StreamIQCheckoutService from '../services/integrations/streamiq/StreamIQCheckoutService';

import EntitlementService from '../services/EntitlementService';
import { getEntitlementType } from './functions/getEntitlementType';

// Common services
container.bind(ConfigService).toSelf();
container.bind(WatchHistoryService).toSelf();
container.bind(FavoriteService).toSelf();
container.bind(ContentApiService).toSelf();
container.bind(SettingsService).toSelf();

// Common controllers
container.bind(AppController).toSelf();
container.bind(WatchHistoryController).toSelf();
container.bind(FavoritesController).toSelf();
container.bind(EpgController).toSelf();
container.bind(EntitlementController).toSelf();

// Integration controllers
container.bind(AccountController).toSelf();
container.bind(CheckoutController).toSelf();

// EPG services
container.bind(EpgService).to(JWEpgService).whenNamed(EPG_TYPE.jwp);
container.bind(EpgService).to(ViewNexaEpgService).whenNamed(EPG_TYPE.viewNexa);
container.bind(EpgService).to(StreamIQEpgService).whenNamed(EPG_TYPE.streamIQ);

// Functions
container.bind(INTEGRATION_TYPE).toDynamicValue(getIntegrationType);

// Entitlement
container.bind(ENTITLEMENT_TYPE).toDynamicValue(getEntitlementType);
container.bind(DETERMINE_ENTITLEMENT_TYPE).toConstantValue(isGenericEntitlementType);
container.bind(DETERMINE_ENTITLEMENT_TYPE).toConstantValue(isAWSCognitoEntitlementType);
container.bind(EntitlementService).to(GenericEntitlementService).whenNamed(ENTITLEMENT.GENERIC);
container.bind(EntitlementService).to(AWSCognitoEntitlementService).whenNamed('COGNITO_STRIPE');

// Cleeng integration
container.bind(DETERMINE_INTEGRATION_TYPE).toConstantValue(isCleengIntegrationType);
container.bind(CleengService).toSelf();
container.bind(AccountService).to(CleengAccountService).whenNamed(INTEGRATION.CLEENG);
container.bind(CheckoutService).to(CleengCheckoutService).whenNamed(INTEGRATION.CLEENG);
container.bind(SubscriptionService).to(CleengSubscriptionService).whenNamed(INTEGRATION.CLEENG);

// JWP integration
container.bind(DETERMINE_INTEGRATION_TYPE).toConstantValue(isJwpIntegrationType);
container.bind(JWPAPIService).toSelf();
container.bind(AccountService).to(JWPAccountService).whenNamed(INTEGRATION.JWP);
container.bind(CheckoutService).to(JWPCheckoutService).whenNamed(INTEGRATION.JWP);
container.bind(SubscriptionService).to(JWPSubscriptionService).whenNamed(INTEGRATION.JWP);

/**
 * VD Private code below
 */

// VD OTT integration
container.bind(DETERMINE_INTEGRATION_TYPE).toConstantValue(isVdOttIntegrationType);
container.bind(VDApiService).toSelf();
container.bind(AccountService).to(VDAccountService).whenNamed('VD');

// Amazon Cognito integration
container.bind(DETERMINE_INTEGRATION_TYPE).toConstantValue(isCognitoStripeIntegrationType);
// @todo to rebind/unbind named services, we need the service identifier. But we don't know which services
//   we want to override beforehand... Think of a better solution soon...
export const cognitoAccountServiceId = container.bind(AccountService).to(AWSCognitoAccountService).whenNamed('COGNITO_STRIPE').getIdentifier();
container.bind(SubscriptionService).to(AWSStripeSubscriptionService).whenNamed('COGNITO_STRIPE');
container.bind(CheckoutService).to(AWSStripeCheckoutService).whenNamed('COGNITO_STRIPE');

// Stream IQ integration
container.bind(DETERMINE_INTEGRATION_TYPE).toConstantValue(isStreamIQIntegrationType);
container.bind(StreamIQApiService).toSelf();
container.bind(StreamIQData).toSelf();
container.bind(AccountService).to(StreamIQAccountService).whenNamed('STREAMIQ');
container.bind(StreamIQEntitlementService).toSelf();
container.bind(EntitlementService).to(StreamIQEntitlementService).whenNamed(ENTITLEMENT.STREAMIQ);
container.bind(SubscriptionService).to(StreamIQSubscriptionService).whenNamed('STREAMIQ');
container.bind(CheckoutService).to(StreamIQCheckoutService).whenNamed('STREAMIQ');
